import { action, computed, makeObservable, observable } from 'mobx';

import { ONBOARDING_CONFIG } from '../../features/onboarding/constants';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import { type RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class OnboardingStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    const riveFtueFlag = resolveFlag('interactive_animation_ftue_enabled', true);
    this.setRiveFtueFlag(riveFtueFlag);

    ldClient?.on('ready', () => {
      this.setRiveFtueFlag(resolveFlag('interactive_animation_ftue_enabled', true));
    });

    ldClient?.on('change', (changes) => {
      if ('interactive_animation_ftue_enabled' in changes) {
        this.setRiveFtueFlag(changes.interactive_animation_ftue_enabled.current);
      }
    });
  }

  dialogTypes: FeatureManagerDialogType[] = ['Ftue'];

  @observable
  public finishedFtue = false;

  @observable
  public _step: number = ONBOARDING_CONFIG.CLOSED;

  @observable
  public currentFtueType: 'interactiveAnimation' | 'general' | null = null;

  @observable
  public riveFtueFlag = false;

  @action
  private setRiveFtueFlag(enabled: boolean) {
    this.riveFtueFlag = enabled;
  }

  @action
  public setCurrentFtueType(type: 'interactiveAnimation' | 'general' | null) {
    this.currentFtueType = type;
  }

  @action
  public setFinishedFtue(finished: boolean) {
    this.finishedFtue = finished;
  }

  @action
  public setStep(step: number) {
    this._step = step;
  }

  @computed
  private get isOnboardingAvailable() {
    const isScModeAvailable = resolveFlag('sc_mode_available', true);
    const { isOnboardingCompleted } = this.rootStore.userStore.profile ?? {};
    return !!this.rootStore.userStore.profile && !isOnboardingCompleted && isScModeAvailable;
  }

  @computed
  public get step() {
    if (!this.isOnboardingAvailable) {
      return ONBOARDING_CONFIG.CLOSED;
    }
    return this._step;
  }

  public finishFtue() {
    this.setStep(ONBOARDING_CONFIG.CLOSED);
    this.setFinishedFtue(true);
    this.setCurrentFtueType(null);
    this.rootStore.featureManagerStore.onDialogFinished('Ftue');
  }

  public nextStep() {
    if (this.step === ONBOARDING_CONFIG.CLOSED) {
      return;
    }

    this.setStep(this.step + 1);
  }

  private startInteractiveAnimationFTUE() {
    this.setCurrentFtueType('interactiveAnimation');
  }

  private shouldShowGameModeSelection(): boolean {
    const showGameModeSelectionFlag = resolveFlag('enable_game_mode_selection', false);
    return showGameModeSelectionFlag;
  }

  startDialogs() {
    if (!this.finishedFtue && this.isOnboardingAvailable) {
      if (this.shouldShowGameModeSelection()) {
        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenGameModeSelectionDialog,
        });
        return true;
      }

      if (this.riveFtueFlag) {
        this.startInteractiveAnimationFTUE();
        return true;
      }

      if (this.step === ONBOARDING_CONFIG.CLOSED) {
        this.setStep(0);
      }

      return true;
    }

    return false;
  }

  public fetch() {
    const ftueFeatureFlag = resolveFlag<'on' | 'off'>('ftue', 'off');

    if (ftueFeatureFlag === 'off') {
      this.finishFtue();
      return 'OFF';
    }

    return 'READY';
  }
}
